import get from 'lodash/get';
import { Module } from 'vuex';
import { ActionContext, DefineActions, DefineGetters, DefineMutations } from 'vuex-type-helper';
import { RootState } from '../index';
import { IAirport, ICombination, IDestination, IFlight, ITour } from '../models';

export interface ModuleState {
    loading: boolean;

    airports: IAirport[];

    combinations: ICombination[];

    destinations: IDestination[];

    flights: IFlight[];

    tours: ITour[];

    salesScore: number;
}

export interface ModuleMutations {
    changeLoading: boolean;

    populateAirports: IAirport[];

    populateCombinations: ICombination[];

    populateDestinations: IDestination[];

    populateFlights: IFlight[];

    populateTours: ITour[];

    changeSalesScore: number;
}

const mutations: DefineMutations<ModuleMutations, ModuleState> = {
    populateAirports(s: ModuleState, records): void {
        s.airports = records;
    },
    populateCombinations(s: ModuleState, records): void {
        s.combinations = records;
    },
    populateDestinations(s: ModuleState, records): void {
        s.tours = [];
        s.flights = [];
        s.combinations = [];
        s.airports = [];
        s.destinations = records;
    },
    populateFlights(s: ModuleState, records): void {
        s.airports = [];
        s.flights = records;
    },
    populateTours(s: ModuleState, records): void {
        s.flights = [];
        s.combinations = [];
        s.airports = [];
        s.tours = records;
    },
    changeLoading(s: ModuleState, value): void {
        // console.debug('loading', value);
        s.loading = value;
    },

    changeSalesScore(s: ModuleState, value): void {
        s.salesScore = value;
    },
};

const API_URL = '/api/enquire';

const prepareURLString = (data: number[], pattern = '&ids[]=') => {
    return data
        .reduce((acc, item) => {
            acc += `${pattern}${item}`;
            return acc;
        }, '')
        .slice(1); // remove first &
};

const actions: DefineActions<any, ModuleState, ModuleMutations, any, any> = {
    loadDestinations(ctx: ActionContext<ModuleState, any, any, ModuleMutations>): Promise<IDestination[]> {
        const url = `${API_URL}/destinations`;
        ctx.commit('changeLoading', true);

        return fetch(url)
            .then((res) => res.json())
            .then((res) => res.body)
            .then((res) => {
                ctx.commit('populateDestinations', res);
                return res;
            })
            .finally(() => {
                ctx.commit('changeLoading', false);
            });
    },

    loadTours(ctx: ActionContext<ModuleState, any, any, ModuleMutations>, payload): Promise<ITour[]> {
        const { tourId, destinationId } = payload;
        // console.debug('orm/loadTours', payload);
        if (!destinationId && !tourId) {
            return Promise.reject();
        }

        let params = ``;
        if (tourId) {
            params += `ids[]=${tourId}`;
        }

        if (destinationId) {
            if (params.length) {
                params += `&`;
            }

            params += `destinations[]=${destinationId}`;
        }

        const url = `${API_URL}/tours?${params}`;
        ctx.commit('changeLoading', true);

        return fetch(url)
            .then((res) => res.json())
            .then((res) => res.body)
            .then((res) => {
                ctx.commit('populateTours', res);
                return res;
            })
            .finally(() => {
                ctx.commit('changeLoading', false);
            });
    },

    loadFlights(ctx: ActionContext<ModuleState, any, any, ModuleMutations>, payload): void {
        const { tourId } = payload;
        // console.debug('orm/loadFlights', payload);
        if (!tourId) {
            return;
        }

        const url = `${API_URL}/departure-dates?tours[]=${tourId}`;
        ctx.commit('changeLoading', true);

        fetch(url)
            .then((res) => res.json())
            .then((res) => res.body)
            .then((res) => {
                ctx.commit('populateFlights', res);
            })
            .finally(() => {
                ctx.commit('changeLoading', false);
            });
    },

    loadAirports(ctx: ActionContext<ModuleState, any, any, ModuleMutations>, payload): void {
        const { airportSids } = payload;
        // console.debug('orm/loadAirports', payload);
        if (!airportSids || !Array.isArray(airportSids)) {
            return;
        }

        const url = `${API_URL}/airports?${prepareURLString(airportSids, '&sids[]=')}`;
        ctx.commit('changeLoading', true);

        fetch(url)
            .then((res) => res.json())
            .then((res) => res.body)
            .then((res) => {
                ctx.commit('populateAirports', res);
            })
            .finally(() => {
                ctx.commit('changeLoading', false);
            });
    },

    loadCombinations(ctx: ActionContext<ModuleState, any, any, ModuleMutations>, payload): void {
        const { tourId } = payload;
        // console.debug('orm/loadCombinations', payload);
        if (!tourId) {
            return;
        }

        const tour = ctx.getters.tours.find((item) => item.id === tourId);
        if (!tour) {
            return;
        }

        const combinationsIds = get(tour, 'combinationIds', []);
        if (!combinationsIds) {
            return;
        }

        const url = `${API_URL}/combinations?${prepareURLString(combinationsIds, '&ids[]=')}`;
        ctx.commit('changeLoading', true);

        fetch(url)
            .then((res) => res.json())
            .then((res) => res.body)
            .then((res) => {
                ctx.commit('populateCombinations', res);
            })
            .finally(() => {
                ctx.commit('changeLoading', false);
            });
    },

    updateSalesScore(ctx: ActionContext<ModuleState, any, any, ModuleMutations>, payload) {
        ctx.commit('changeSalesScore', payload.score);
    },
};

const getters: DefineGetters<any, ModuleState> = {
    isLoading({ loading }) {
        return loading;
    },

    destinations({ destinations }) {
        return destinations;
    },

    tours({ tours }) {
        return tours;
    },

    flights({ flights }) {
        return flights;
    },

    airports({ airports }) {
        return airports;
    },

    combinations({ combinations }) {
        return combinations;
    },

    salesScore({ salesScore }) {
        return salesScore;
    },
};

const state: ModuleState = {
    loading: false,
    airports: [],
    combinations: [],
    destinations: [],
    flights: [],
    tours: [],
    salesScore: 0,
};

const module: Module<ModuleState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};

export default module;
